import { h } from "preact";
import { useEffect, useRef, useCallback, useState } from "preact/hooks";
import type { ChatMessage, WidgetConfig, StudentIdentity } from "../types";
import { MessageBubble } from "./MessageBubble";
import { ChatInput } from "./ChatInput";

interface ChatPanelProps {
  config: WidgetConfig;
  messages: ChatMessage[];
  isStreaming: boolean;
  error: string | null;
  retryable?: boolean;
  onSend: (content: string) => void;
  onClose: () => void;
  onClearError: () => void;
  onRetry?: () => void;
  onNewChat: () => void;
  position?: "bottom-right" | "bottom-left";
  maxMessages?: number;
  totalUserMessages: number;
  sessionExhausted?: boolean;
  showInfo: boolean;
  onToggleInfo: () => void;
  // Student self-identification (optional, for extra-credit attribution)
  identityRecord: StudentIdentity | null;
  identityActive: boolean;
  showIdentityGate: boolean;
  onApplyIdentity: (identity: StudentIdentity) => void;
  onReactivateIdentity: () => void;
  onGoAnonymous: () => void;
  onOpenIdentityGate: () => void;
  onCloseIdentityGate: () => void;
  /** Verified name from UW NetID sign-in, when the student has signed in. */
  signedInName?: string | null;
  signingIn?: boolean;
  signInError?: string | null;
  /**
   * A plain statement of what just changed about identity — shown when switching
   * between anonymous and signed-in starts a new conversation. Not an error: the chat
   * clearing on its own is startling enough to deserve a sentence.
   */
  identityNote?: string | null;
  onSignIn: () => void;
  /** False when the section requires attributed conversations. */
  allowAnonymousChat?: boolean;
  /** The student's model choice; null means the section default is in effect. */
  chatModel?: string | null;
  onChatModelChange?: (key: string) => void;
}

/** SVG eye icon for privacy notice */
function EyeIcon() {
  return (
    <svg class="widget-privacy-icon" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" />
    </svg>
  );
}

/** SVG circle-i info icon */
function InfoIcon() {
  return (
    <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z" />
    </svg>
  );
}

/** SVG refresh/new-chat icon */
function NewChatIcon() {
  return (
    <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
    </svg>
  );
}

/** SVG close (X) icon */
function CloseIcon() {
  return (
    <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
    </svg>
  );
}

/** SVG maximize (expand) icon */
function MaximizeIcon() {
  return (
    <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z" />
    </svg>
  );
}

/** SVG restore (collapse) icon */
function RestoreIcon() {
  return (
    <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <path d="M5 16h3v3h2v-5H5v2zm3-8H5v2h5V5H8v3zm6 11h2v-3h3v-2h-5v5zm2-11V5h-2v5h5V8h-3z" />
    </svg>
  );
}

/** SVG chat bubble icon for welcome state */
function WelcomeIcon() {
  return (
    <svg
      class="widget-welcome-icon"
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
      fill="currentColor"
    >
      <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H6l-2 2V4h16v12z" />
    </svg>
  );
}

function buildSubtitle(config: WidgetConfig): string {
  const parts: string[] = [];
  if (config.courseCode) parts.push(config.courseCode);
  if (config.sectionName) parts.push(config.sectionName);
  if (config.assignmentName) parts.push(config.assignmentName);
  return parts.join(" > ");
}

export function ChatPanel({
  config,
  messages,
  isStreaming,
  error,
  retryable,
  onSend,
  onClose,
  onClearError,
  onRetry,
  onNewChat,
  position,
  maxMessages,
  totalUserMessages,
  sessionExhausted,
  showInfo,
  onToggleInfo,
  identityRecord,
  identityActive,
  showIdentityGate,
  onApplyIdentity,
  onReactivateIdentity,
  onGoAnonymous,
  onOpenIdentityGate,
  onCloseIdentityGate,
  signedInName,
  signingIn,
  signInError,
  identityNote,
  onSignIn,
  allowAnonymousChat = true,
  chatModel,
  onChatModelChange,
}: ChatPanelProps) {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState<{ width: number; height: number } | null>(null);
  const [maximized, setMaximized] = useState(false);
  const [nameInput, setNameInput] = useState(identityRecord?.studentName || "");
  const [numberInput, setNumberInput] = useState(identityRecord?.studentNumber || "");

  // Identified by either route: UW verified it, or the student typed it. Without
  // signedInName here, a signed-in student with no typed name reads as "Anonymous".
  const isIdentified = identityActive && (!!identityRecord || !!signedInName);
  const identityLabel = identityRecord
    ? [identityRecord.studentName, identityRecord.studentNumber].filter(Boolean).join(" · ")
    : "";

  const submitIdentity = useCallback(() => {
    const studentName = nameInput.trim();
    const studentNumber = numberInput.trim();
    if (!studentName && !studentNumber) return;
    onApplyIdentity({ studentName, studentNumber });
  }, [nameInput, numberInput, onApplyIdentity]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length, messages[messages.length - 1]?.content]);

  // Drag-to-resize from the top-left corner (panel is anchored bottom-right)
  const handleResizeStart = useCallback(
    (e: MouseEvent) => {
      e.preventDefault();
      const panel = panelRef.current;
      if (!panel) return;

      const startX = e.clientX;
      const startY = e.clientY;
      const startWidth = panel.offsetWidth;
      const startHeight = panel.offsetHeight;
      const isLeft = position === "bottom-left";

      const onMove = (ev: MouseEvent) => {
        const dx = ev.clientX - startX;
        const dy = ev.clientY - startY;
        // For bottom-right panel: dragging left (negative dx) increases width
        // For bottom-left panel: dragging right (positive dx) increases width
        const newWidth = isLeft
          ? Math.max(320, Math.min(startWidth + dx, window.innerWidth - 40))
          : Math.max(320, Math.min(startWidth - dx, window.innerWidth - 40));
        // Dragging up (negative dy) increases height
        const newHeight = Math.max(300, Math.min(startHeight - dy, window.innerHeight - 100));
        setSize({ width: newWidth, height: newHeight });
      };

      const onUp = () => {
        document.removeEventListener("mousemove", onMove);
        document.removeEventListener("mouseup", onUp);
      };

      document.addEventListener("mousemove", onMove);
      document.addEventListener("mouseup", onUp);
    },
    [position]
  );

  const subtitle = buildSubtitle(config);
  const positionClass = position === "bottom-left" ? " widget-panel--left" : "";
  const sizeStyle = size ? { width: `${size.width}px`, height: `${size.height}px` } : {};
  const clientAtLimit = maxMessages != null && maxMessages > 0 && totalUserMessages >= maxMessages;
  const atLimit = sessionExhausted || clientAtLimit;
  // A section that requires attribution must actually require it. Hiding the
  // anonymity toggle while leaving the input live would be a setting that looks
  // enforced and is not.
  const mustSignIn = !allowAnonymousChat && !isIdentified;

  return (
    <div
      ref={panelRef}
      class={`widget-panel${positionClass}${maximized ? " widget-panel--maximized" : ""}`}
      style={maximized ? undefined : sizeStyle}
    >
      {/* Resize handle — top-left for bottom-right panel, top-right for bottom-left.
          Hidden while maximized (dimensions are viewport-driven then). */}
      {!maximized && (
        <div
          class={`widget-resize-handle${position === "bottom-left" ? " widget-resize-handle--right" : ""}`}
          onMouseDown={handleResizeStart}
        />
      )}
      {/* Header */}
      <div class="widget-header">
        <div class="widget-header-info">
          <div class="widget-header-title">
            <button class="widget-info-btn" onClick={onToggleInfo} title="About this tutor">
              <InfoIcon />
            </button>
            UWT Tutor AI
          </div>
          {subtitle && <div class="widget-header-subtitle">{subtitle}</div>}
        </div>
        <div class="widget-header-actions">
          {/* Shown only where the instructor has chosen a model for the section.
              Locked mid-reply so the choice a student sees is the one that reply
              is using. */}
          {config.chatModels && onChatModelChange && (
            <select
              class="widget-model-select"
              title="Tutor model"
              aria-label="Tutor model"
              value={chatModel ?? config.chatModels.default}
              disabled={isStreaming}
              onChange={(e) => onChatModelChange((e.target as HTMLSelectElement).value)}
            >
              {config.chatModels.options.map((o) => (
                <option key={o.key} value={o.key}>
                  {o.label.replace(/^Claude /, "")}
                </option>
              ))}
            </select>
          )}
          {messages.length > 0 && !atLimit && (
            <button class="widget-close-btn" onClick={onNewChat} title="New chat">
              <NewChatIcon />
            </button>
          )}
          <button
            class="widget-close-btn"
            onClick={() => setMaximized((m) => !m)}
            title={maximized ? "Restore size" : "Maximize"}
          >
            {maximized ? <RestoreIcon /> : <MaximizeIcon />}
          </button>
          <button class="widget-close-btn" onClick={onClose} title="Close chat">
            <CloseIcon />
          </button>
        </div>
      </div>

      {/* Demo banner */}
      <div class="widget-demo-banner">
        Preview — This feature is in beta - v{__WIDGET_VERSION__}
      </div>

      {/* Identity bar.
          Three sources of identity, in order of trust: signed in with UW NetID
          (verified), a name the student typed (asserted), or nothing (anonymous).
          Anonymous is a real state, not a pending one — the conversation runs on a
          guest session and stores no link to any account. */}
      <div class="widget-identity-bar">
        <span class="widget-identity-status">
          {signedInName && isIdentified
            ? `Signed in as ${signedInName}`
            : isIdentified
              ? `Chatting as ${identityLabel}`
              : "Anonymous"}
        </span>

        {isIdentified ? (
          allowAnonymousChat ? (
            <button
              class="widget-identity-toggle"
              onClick={onGoAnonymous}
              title="Keep chatting, but stop attributing this conversation to you"
            >
              Go anonymous
            </button>
          ) : null
        ) : signedInName ? (
          <button class="widget-identity-toggle" onClick={onReactivateIdentity} title="Attribute this conversation to you">
            Use my name
          </button>
        ) : (
          <>
            <button
              class="widget-identity-toggle widget-identity-toggle--cta"
              onClick={onSignIn}
              disabled={signingIn}
              title="Verify who you are with your UW NetID"
            >
              {signingIn ? "Signing in…" : "Sign in with UW NetID"}
            </button>
            {allowAnonymousChat && identityRecord ? (
              <button class="widget-identity-toggle" onClick={onReactivateIdentity} title="Use the name you entered before">
                Use my name
              </button>
            ) : allowAnonymousChat ? (
              <button class="widget-identity-toggle" onClick={onOpenIdentityGate} title="Type your name instead of signing in">
                Enter name
              </button>
            ) : null}
          </>
        )}
      </div>

      {signInError && (
        <div class="widget-identity-gate-note" style="color:#b3261e;padding:0 12px 6px">
          {signInError}
        </div>
      )}

      {identityNote && !signInError && (
        <div class="widget-identity-gate-note" style="padding:0 12px 6px">
          {identityNote}
        </div>
      )}

      {/* A section can require attribution. Say so plainly rather than leaving the
          student wondering why there is no anonymous option. */}
      {mustSignIn && (
        <div class="widget-identity-gate-note" style="padding:0 12px 8px">
          This course asks students to sign in before chatting.
        </div>
      )}

      {/* Identity gate — name + number entry */}
      {showIdentityGate && (
        <div class="widget-identity-gate">
          <div class="widget-identity-gate-title">Add your name to earn credit</div>
          <input
            class="widget-identity-input"
            type="text"
            placeholder="Full name"
            value={nameInput}
            onInput={(e) => setNameInput((e.target as HTMLInputElement).value)}
          />
          <input
            class="widget-identity-input"
            type="text"
            placeholder="Student number"
            value={numberInput}
            onInput={(e) => setNumberInput((e.target as HTMLInputElement).value)}
          />
          <div class="widget-identity-gate-actions">
            <button class="widget-identity-save" onClick={submitIdentity} disabled={!nameInput.trim() && !numberInput.trim()}>
              Save
            </button>
            <button class="widget-identity-skip" onClick={onCloseIdentityGate}>
              Skip
            </button>
          </div>
          <div class="widget-identity-gate-note">
            You control this — switch back to anonymous anytime with one click.
          </div>
        </div>
      )}

      {/* Info panel */}
      {showInfo && (
        <div class="widget-info-panel">
          <p><strong>About this tutor</strong></p>
          <p>
            This AI tutor is provided by your professor to support your learning.
            It connects to a large language model to help you think through
            problems — it won't give you answers, but it will help you build understanding.
          </p>
          <p><strong>Keep in mind:</strong></p>
          <ul>
            <li>AI responses may contain errors — always verify against course materials</li>
            <li>This tool supplements your learning; it doesn't replace office hours, TAs, or your own critical thinking</li>
            <li class="widget-info-warning">Your conversations may be reviewed by your professor to improve the course</li>
          </ul>
        </div>
      )}

      {/* Error banner */}
      {error && (
        <div class={`widget-error${retryable ? " widget-error--retryable" : ""}`}>
          <span>{error}</span>
          <div class="widget-error-actions">
            {retryable && onRetry && (
              <button class="widget-error-retry" onClick={onRetry}>
                Try again
              </button>
            )}
            <button class="widget-error-dismiss" onClick={onClearError}>
              &times;
            </button>
          </div>
        </div>
      )}

      {/* Messages or welcome */}
      {messages.length === 0 ? (
        <div class="widget-welcome">
          <WelcomeIcon />
          <div class="widget-welcome-text">
            Ask me anything about {config.courseName || "this course"}! I'm here
            to help you learn.
          </div>
          <div class="widget-privacy-notice">
            <EyeIcon />
            <span>
              {isIdentified ? (
                <>Your professor can view all conversations. You're identified as <strong>{identityLabel}</strong> — use “Go anonymous” above to detach your name anytime.</>
              ) : (
                <>Your professor can view all conversations. You're currently <strong>anonymous</strong>. Add your name above to get credit for using the tutor.</>
              )}
            </span>
          </div>
        </div>
      ) : (
        <div class="widget-messages">
          {messages.map((msg) => (
            <MessageBubble
              key={msg.id}
              message={msg}
              isStreaming={isStreaming && msg === messages[messages.length - 1]}
            />
          ))}
          {atLimit && (
            <div class="widget-limit-notice">
              {sessionExhausted
                ? "Session limit reached. Please return later for a new session."
                : `Message limit reached (${maxMessages} messages). Please try again later.`}
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
      )}

      {/* Input */}
      <ChatInput
        onSend={onSend}
        disabled={isStreaming || atLimit || mustSignIn}
        messageCount={maxMessages != null ? totalUserMessages : undefined}
        messageLimit={maxMessages}
      />
    </div>
  );
}
